import axios from "axios";
import fileDownload from "js-file-download";
import Cookies from "js-cookie";
import { message } from "antd";
import API_MANAGER from "../API";

const ACCESS_TOKEN_DAYS = 3 / 24;
const REFRESH_TOKEN_DAYS = 6 / 24;
const ACCESS_TOKEN_MS = 3 * 3600 * 1000;

let isRefreshing = false;
let refreshSubscribers = [];

const subscribeTokenRefresh = (cb) => {
  refreshSubscribers.push(cb);
};

const onTokenRefreshed = (token) => {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
};

const clearSessionAndRedirect = () => {
  const email = localStorage.getItem("email");
  const password = localStorage.getItem("password");
  localStorage.clear();
  Cookies.remove("access_token");
  Cookies.remove("refresh_token");
  if (email && password) {
    localStorage.setItem("email", email);
    localStorage.setItem("password", password);
  }
  message.error("Your session is expired, please login again.");
  window.open(`/login`, "_self");
};

const isRefreshRequest = (config) => {
  const url = config?.url || "";
  return url.includes("refresh-token") || url.includes("admin-auth/login");
};

let HELPERS = {
  getCookie: (name) => {
    let cookieValue = null;
    if (document.cookie && document.cookie !== "") {
      const cookies = document.cookie.split(";");
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim();
        if (cookie.substring(0, name.length + 1) === name + "=") {
          cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
          break;
        }
      }
    }
    return cookieValue;
  },
  persistAuthTokens: (accessToken, refreshToken) => {
    if (accessToken) {
      Cookies.set("access_token", accessToken, {
        expires: ACCESS_TOKEN_DAYS,
      });
    }
    if (refreshToken) {
      Cookies.set("refresh_token", refreshToken, {
        expires: REFRESH_TOKEN_DAYS,
      });
    }
    localStorage.setItem(
      "expireTime",
      String(new Date().getTime() + ACCESS_TOKEN_MS)
    );
  },
  request: (config) => {
    config.headers = config.headers ? config.headers : {};

    return axios
      .request(config)
      .then((response) => {
        return response;
      })
      .catch(async (err) => {
        if (err?.response?.status === 401 && !isRefreshRequest(config)) {
          const refreshToken = Cookies.get("refresh_token");
          if (refreshToken && config.headers?.Authorization) {
            try {
              if (isRefreshing) {
                return new Promise((resolve, reject) => {
                  subscribeTokenRefresh((token) => {
                    if (token) {
                      config.headers["Authorization"] = `Bearer ${token}`;
                      axios.request(config).then(resolve).catch(reject);
                    } else {
                      reject(err);
                    }
                  });
                });
              }

              await HELPERS.refreshAccessToken(refreshToken);
              const newAccessToken = Cookies.get("access_token");
              if (newAccessToken) {
                config.headers["Authorization"] = `Bearer ${newAccessToken}`;
                return axios.request(config);
              }
            } catch (refreshError) {
              clearSessionAndRedirect();
              throw refreshError;
            }
          }
        }
        throw err;
      });
  },
  secureRequest: async (config) => {
    config.headers = config.headers ? config.headers : {};
    let accessToken = Cookies.get("access_token");
    if (accessToken === undefined) {
      const refreshToken = Cookies.get("refresh_token");
      if (refreshToken === undefined) {
        clearSessionAndRedirect();
        return;
      }
      try {
        await HELPERS.refreshAccessToken(refreshToken);
        accessToken = Cookies.get("access_token");
      } catch (error) {
        clearSessionAndRedirect();
        throw error;
      }
    }
    config.headers["Authorization"] = `Bearer ${accessToken} `;
    return HELPERS.request(config);
  },
  converToFormData: (obj, rootName, ignoreList) => {
    var formData = new FormData();
    function appendFormData(data, root) {
      if (!ignore(root)) {
        root = root || "";
        if (data instanceof File) {
          formData.append(root, data);
        } else if (Array.isArray(data)) {
          for (var i = 0; i < data.length; i++) {
            appendFormData(data[i], root + "[" + i + "]");
          }
        } else if (typeof data === "object" && data) {
          for (var key in data) {
            if (Object.prototype.hasOwnProperty.call(data, key)) {
              if (root === "") {
                appendFormData(data[key], key);
              } else {
                appendFormData(data[key], root + "." + key);
              }
            }
          }
        } else {
          if (data !== null && typeof data !== "undefined") {
            formData.append(root, data);
          }
        }
      }
    }
    function ignore(root) {
      return (
        Array.isArray(ignoreList) &&
        ignoreList.some(function (x) {
          return x === root;
        })
      );
    }
    appendFormData(obj, rootName);
    return formData;
  },
  debounce: (fn, wait) => {
    let timeout;
    return function () {
      const context = this,
        args = arguments;
      clearTimeout(timeout);
      timeout = setTimeout(function () {
        fn.apply(context, args);
      }, wait);
    };
  },
    downloadFile: (response) => {
      try {
        let fileName = null;
        const fieldToFind = "filename";
        const headerValues = response.headers["content-disposition"];
        if (headerValues && headerValues !== "") {
          const headerValuesArray = headerValues.split(";");
          for (let i = 0; i < headerValuesArray.length; i++) {
            const headerValue = headerValuesArray[i].trim();
            if (
              headerValue.substring(0, fieldToFind.length + 1) ===
              fieldToFind + "="
            ) {
              fileName = headerValue.substring(fieldToFind.length + 1);
              break;
            }
          }
        }
        var blob = new Blob([response?.data]);
        fileDownload(blob, `${fileName}`);
      } catch (err) {
        message.warn("Something went wrong");
      }
    },
  deleteCookie: (name) => {
    document.cookie = name + "=; expires=Thu, 01 Jan 1970 00:00:01 GMT;";
  },
  refreshAccessToken: async (refreshToken) => {
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        subscribeTokenRefresh((token) => {
          if (token) {
            resolve({ data: { data: { accessToken: token } } });
          } else {
            reject(new Error("Token refresh failed"));
          }
        });
      });
    }

    isRefreshing = true;

    try {
      const response = await API_MANAGER.refreshToken({
        refreshToken,
      });
      const accessToken =
        response?.data?.data?.accessToken || response?.data?.access_token;
      const newRefreshToken =
        response?.data?.data?.refreshToken || response?.data?.refresh_token;

      HELPERS.persistAuthTokens(accessToken, newRefreshToken);
      onTokenRefreshed(accessToken);
      isRefreshing = false;
      return response;
    } catch (err) {
      onTokenRefreshed(null);
      isRefreshing = false;
      throw err;
    }
  },
};

export default HELPERS;
